import CloseIcon from "@mui/icons-material/Close";
import ControlPointRoundedIcon from "@mui/icons-material/ControlPointRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import FilterListIcon from "@mui/icons-material/FilterListRounded";
import { Box, Menu, MenuItem } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Pager from "components/adminUi/Pager";
import Button from "components/ui/Button";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { DictionaryMode } from "contexts/DictionaryToolsContext";
import { MouseEvent, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { personalDictionaryQuery, useAddSavedWord } from "services/dictionary";
import { logError } from "services/logging";
import { getErrorComponents } from "services/util";
import quizIcon from "../images/quiz.svg";
import { ExerciseDialog } from "./ExerciseDialog";
import { AddNewWordDialog } from "./SaveWords/AddNewWordDialog";
import { SaveWords } from "./SaveWords/SaveWords";
import "../tabs.css";
import { dynamicKey } from "services/i18n";

const PAGE_SIZE = 10;

type SaveTabProps = {
  myWordsSearchText: string;
  onModeChanged: (mode: DictionaryMode) => void;
  onTextChanged: (text: string) => void;
};

export default function SaveTab(props: SaveTabProps) {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [selectedSort, setSelectedSort] = useState<SortId>("newest");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedWordId, setExpandedWordId] = useState<string | null>(null);
  const [deletedWord, setDeletedWord] = useState<DeletedWord | null>(null);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openPracticeDialog, setOpenPracticeDialog] = useState(false);

  const deleteToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const wordsQuery = useQuery(
    personalDictionaryQuery({
      page,
      size: PAGE_SIZE,
      filter: props.myWordsSearchText || undefined,
      ...IDS_TO_SORTING[selectedSort].sorting,
    })
  );
  const wordsList = wordsQuery.data?.list ?? [];
  const totalCount = wordsQuery.data?.total_count ?? 0;

  const addWord = useAddSavedWord();

  const changePage = (pageNumber: number) => {
    setPage(pageNumber);
    setExpandedWordId(null);
  };

  const onDeleteWord = (word: DeletedWord) => {
    if (deleteToastTimerRef.current) {
      clearTimeout(deleteToastTimerRef.current);
    }
    setDeletedWord(word);
    setOpenDeletePopup(true);
    setExpandedWordId(null);
    deleteToastTimerRef.current = setTimeout(() => {
      setOpenDeletePopup(false);
    }, 6000);
  };

  const onOpenSortBy = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onCloseSortBy = () => {
    setAnchorEl(null);
  };

  const onSortBy = (event: MouseEvent<HTMLLIElement>) => {
    const id = (event.target as HTMLElement).id;
    if (!isSortId(id)) {
      logError("Unknown item.id", id);
      return;
    }
    setSelectedSort(id);
    setPage(1);
    setExpandedWordId(null);
    onCloseSortBy();
  };

  const onChangePanel = (wordId: string, expanded: boolean) => {
    setExpandedWordId(expanded ? wordId : null);
  };

  const onCloseDeletePopup = () => {
    setOpenDeletePopup(false);
  };

  const undoDelete = () => {
    onCloseDeletePopup();
    if (!deletedWord) {
      return;
    }
    addWord.mutate({
      word: deletedWord.word,
      notes: deletedWord.notes ?? undefined,
    });
  };

  const onCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  return (
    <div className="translate-items" id="translate-items">
      <Box
        className="vocab-toolbar"
        sx={{
          display: "flex",
          width: "100%",
          mb: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <button
            className="sort-button"
            aria-controls="simple-menu"
            aria-haspopup="true"
            onClick={onOpenSortBy}
          >
            <span>
              <FilterListIcon className="filter-icon" />
            </span>
            <span>{t(dynamicKey(IDS_TO_SORTING[selectedSort].name))}</span>
            <span>
              <ExpandMoreIcon />
            </span>
          </button>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={onCloseSortBy}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {Object.entries(IDS_TO_SORTING).map(([id, value]) => {
              return (
                <MenuItem key={id} id={id} onClick={onSortBy}>
                  {t(dynamicKey(value.name))}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
        <Box className="save-actions">
          <Button
            size="small"
            variant="secondary"
            className="practice-button"
            onClick={() => setOpenPracticeDialog(true)}
          >
            <img src={quizIcon} alt="" />
            {t("Dictionary.vocabList.practice")}
          </Button>
          <Button
            size="small"
            className="new-vocab-button"
            onClick={() => setOpenAddDialog(true)}
          >
            <ControlPointRoundedIcon
              sx={{ fontSize: 14, color: "var(--text-inverse)" }}
            />
            {t("Dictionary.vocabList.title")}
          </Button>
        </Box>
      </Box>
      {wordsQuery.isError && getErrorComponents(wordsQuery.error)}
      {wordsQuery.isPending ? (
        <div className="dictionary-loading">
          <TalaeraSpinner />
        </div>
      ) : (
        <div className="dictionary-paginated-list">
          <ul>
            {wordsList.map(word => (
              <SaveWords
                key={word.id}
                {...word}
                expanded={word.id === expandedWordId}
                onChangePanel={(_, isExpanded) => onChangePanel(word.id, isExpanded)}
                onModeChanged={props.onModeChanged}
                onDeleteWord={onDeleteWord}
                onTextChanged={props.onTextChanged}
              />
            ))}
          </ul>
          {openDeletePopup && deletedWord && (
            <div className="delete-toast">
              <span className="toast-message">
                <Trans
                  i18nKey="Dictionary.vocabList.wordRemoved"
                  values={{ word: deletedWord.word }}
                />
              </span>
              <Button variant="plain" className="undo-button" onClick={undoDelete}>
                {t("Dictionary.vocabList.undo")}
              </Button>
              <button className="close-button" onClick={onCloseDeletePopup}>
                <CloseIcon />
              </button>
            </div>
          )}
          {totalCount > PAGE_SIZE && (
            <Pager
              page={page}
              pageCount={Math.ceil(totalCount / PAGE_SIZE)}
              onPageChange={changePage}
              prevLabel={`‹ ${t("Common.actions.previous")}`}
              nextLabel={`${t("Common.actions.next")} ›`}
            />
          )}
        </div>
      )}
      {openAddDialog && (
        <AddNewWordDialog open={openAddDialog} onClose={onCloseAddDialog} />
      )}
      {openPracticeDialog && (
        <ExerciseDialog
          smallVocab={totalCount < 5}
          open={openPracticeDialog}
          onClose={() => setOpenPracticeDialog(false)}
          explanation={""}
        />
      )}
    </div>
  );
}

type Sorting = {
  order_by: "created" | "updated" | null;
  sort: "asc" | "desc";
};

const IDS_TO_SORTING = {
  newest: {
    sorting: { order_by: "created", sort: "desc" },
    name: "Dictionary.vocabList.sortNewest",
  },
  oldest: {
    sorting: { order_by: "created", sort: "asc" },
    name: "Dictionary.vocabList.sortOldest",
  },
  alpha: {
    sorting: { order_by: null, sort: "asc" },
    name: "Common.labels.sortAlphaAsc",
  },
  alpha_desc: {
    sorting: { order_by: null, sort: "desc" },
    name: "Common.labels.sortAlphaDesc",
  },
} satisfies Record<string, { sorting: Sorting; name: string }>;

type SortId = keyof typeof IDS_TO_SORTING;

function isSortId(id: string): id is SortId {
  return id in IDS_TO_SORTING;
}

// Structural subset of what SaveWords passes to onDeleteWord; only these two
// fields are needed to render the undo toast and restore the word.
type DeletedWord = {
  word: string;
  notes: string | null;
};
